/**
 * Разбор и форматирование даты для поля ввода (centrika-docs, `common.md` → «Поля даты», MR !10).
 *
 * Интерфейс показывает дату как `дд.мм.гггг`, а код обменивается ISO `YYYY-MM-DD` — на нём завязаны
 * контракт с сервером и проверки `date-order.ts`. Здесь живёт преобразование между этими формами,
 * общее для компонента поля и тестов.
 *
 * Несуществующие даты (`31.02.2026`) не распознаются — правило то же, что в `date-order.ts`:
 * компоненты после `new Date` должны совпасть с исходными.
 */

const RU_DATE = /^(\d{2})\.(\d{2})\.(\d{4})$/
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/** Существует ли такая календарная дата (отсекает `31.02`, `31.04` и подобное). */
function isRealDate(year: number, month: number, day: number): boolean {
  const d = new Date(year, month - 1, day)
  return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day
}

/**
 * `дд.мм.гггг` → ISO `YYYY-MM-DD`. Пустая строка — если ввод неполный, содержит лишнее или дата
 * не существует: незавершённый набор не является ошибкой, он просто ещё не даёт значения.
 */
export function parseRuDate(value: string): string {
  const m = RU_DATE.exec(value.trim())
  if (!m) return ''
  const [, dd, mm, yyyy] = m
  const day = Number(dd)
  const month = Number(mm)
  const year = Number(yyyy)
  if (!isRealDate(year, month, day)) return ''
  return `${yyyy}-${mm}-${dd}`
}

/** ISO `YYYY-MM-DD` → `дд.мм.гггг`. Пустая строка — если значение пустое или не разбирается. */
export function formatIsoToRu(value: string): string {
  const m = ISO_DATE.exec(value.trim())
  if (!m) return ''
  const [, yyyy, mm, dd] = m
  if (!isRealDate(Number(yyyy), Number(mm), Number(dd))) return ''
  return `${dd}.${mm}.${yyyy}`
}

/**
 * Оставляет только то, что пользователь может набирать в поле даты: цифры и точки, не длиннее
 * `дд.мм.гггг`. Разделители НЕ подставляются автоматически — это была бы маска, которую требование
 * запрещает.
 */
export function keepDateChars(value: string): string {
  return value.replace(/[^\d.]/g, '').slice(0, 10)
}

/**
 * Приводит вставленное из буфера значение к `дд.мм.гггг`. Маской не является: пользователь вставляет
 * готовую дату целиком, и без нормализации `2026-07-28` превратилось бы фильтрацией в `20260728`.
 * Понимает ISO и записи через `/`, `-`, `.`; всё остальное отдаётся отфильтрованным как есть.
 */
export function normalizePastedDate(value: string): string {
  const raw = value.trim()
  const iso = formatIsoToRu(raw)
  if (iso) return iso
  const m = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(raw)
  if (m) {
    const dd = m[1].padStart(2, '0')
    const mm = m[2].padStart(2, '0')
    return `${dd}.${mm}.${m[3]}`
  }
  return keepDateChars(raw)
}
